import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useCreateFiles, useNetwork } from '@tribeplatform/react-sdk/hooks';
import { fileSizeLimitError } from '../../common/lib/upload';
import { useI18n, useStaticIntl } from '../../i18n';
import { findNode } from '../helpers/node';
import { composerError, fileUploadError } from '../utils/errors';
import { getFileReader } from '../utils/filereader';
import { useAddImageToEditor } from './useAddImageToEditor';
const composerFileSizeLimitError = (intl) => (limit) => {
    composerError(intl)(fileSizeLimitError(limit, 'file', intl));
};
export const useHandleFiles = ({ editor, handleAttachmentSelect, }) => {
    const intl = useI18n();
    const { data: network } = useNetwork();
    // We are using this stack to prevent falsifying the loading state while calling the onFileUpload function multiple times.
    const uploadQueue = useRef([]);
    const [loading, setLoading] = useState(false);
    const { addImage } = useAddImageToEditor();
    const { mutateAsync: createFiles } = useCreateFiles();
    const uploadVideo = useCallback(async (file) => {
        const fileSrc = await getFileReader(file);
        const { type, name, size } = file;
        editor
            .chain()
            .focus()
            .setVideo({ id: file.id, src: fileSrc.url, loading: true, type })
            .setTextSelection(editor.state.selection.anchor) // to reset the selection
            .run();
        const [{ id, url }] = await createFiles([
            {
                file,
                contentType: type,
                name,
                extension: name.split('.').pop().toLowerCase() || 'mp4',
                size,
            },
        ]);
        const { offset, node } = findNode(editor.state, node => node.attrs.id === file.id);
        editor
            .chain()
            .setNodeSelection(offset)
            .focus()
            .setVideoAttributes({
            loading: false,
            id,
            src: url,
            type,
        })
            .setTextSelection(offset + node.nodeSize)
            .insertContent(' ')
            .run();
    }, [editor, createFiles]);
    const getSizeLimitError = useStaticIntl(composerFileSizeLimitError);
    const onFileUpload = useCallback(async (filesList, options = {}) => {
        const { asAttachment = false, attrs } = options;
        setLoading(true);
        uploadQueue.current.push(new Date().getTime());
        // If not image, do nothing
        if (!(filesList === null || filesList === void 0 ? void 0 : filesList.length))
            return null;
        const files = Array.from(filesList);
        await Promise.all(files.map(async (file) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            const isImage = !asAttachment && ((_a = file.type) === null || _a === void 0 ? void 0 : _a.startsWith('image'));
            const isVideo = !asAttachment &&
                (file.type.includes('audio') || file.type.includes('video'));
            if (isImage && ((_b = network === null || network === void 0 ? void 0 : network.settings) === null || _b === void 0 ? void 0 : _b.imageSizeLimit) < file.size) {
                getSizeLimitError((_c = network === null || network === void 0 ? void 0 : network.settings) === null || _c === void 0 ? void 0 : _c.imageSizeLimit);
                return;
            }
            if (!isImage && ((_d = network === null || network === void 0 ? void 0 : network.settings) === null || _d === void 0 ? void 0 : _d.fileSizeLimit) < file.size) {
                getSizeLimitError((_e = network === null || network === void 0 ? void 0 : network.settings) === null || _e === void 0 ? void 0 : _e.fileSizeLimit);
                return;
            }
            file.id = uuidv4();
            try {
                if (isVideo) {
                    await uploadVideo(file);
                }
                else if (isImage) {
                    await addImage(editor.view, file, attrs);
                }
                else {
                    await handleAttachmentSelect([file]);
                }
            }
            catch (error) {
                // Show the error notification message
                fileUploadError(intl)({
                    fileName: file.name,
                    message: ((_h = (_g = (_f = error === null || error === void 0 ? void 0 : error.response) === null || _f === void 0 ? void 0 : _f.errors) === null || _g === void 0 ? void 0 : _g[0]) === null || _h === void 0 ? void 0 : _h.message) || error.message,
                });
                if (file.id) {
                    const { offset } = findNode(editor.state, node => node.attrs.id === file.id);
                    // Delete the actual node (image/video tag) from the DOM
                    editor.commands.deleteRange({
                        from: offset,
                        to: offset + 1,
                    });
                }
            }
        })).finally(() => {
            uploadQueue.current.pop();
            if (uploadQueue.current.length === 0) {
                setLoading(false);
            }
        });
    }, [
        uploadVideo,
        addImage,
        handleAttachmentSelect,
        editor,
        network === null || network === void 0 ? void 0 : network.settings,
        intl,
    ]);
    return {
        onFileUpload,
        loading,
    };
};
//# sourceMappingURL=useHandleFiles.js.map